import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  IconButton,
  InputGroup,
  InputRightElement,
  Badge,
  Alert,
  AlertIcon,
  useDisclosure,
  Flex,
  Stack,
  Text,
  NumberInput,
  NumberInputField,
  useColorModeValue,
  Tooltip
} from '@chakra-ui/react';
import {
  FaEdit,
  FaSearch,
  FaTimes,
  FaHistory
} from 'react-icons/fa';
import AdminService from '../../services/AdminService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    subscription_type: '',
    credits: 0,
    is_verified: false
  });
  const [successMessage, setSuccessMessage] = useState('');
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const bgColor = useColorModeValue('white', 'gray.700');

  // Fetch users on component mount and when page, rowsPerPage, or searchTerm changes
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await AdminService.getUsers(page + 1, rowsPerPage, searchTerm);
        setUsers(response.users);
        setTotalUsers(response.total);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again later.');
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, rowsPerPage, searchTerm]);

  // Handle page change
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  // Open edit modal for a user
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      subscription_type: user.subscription_type || 'free',
      credits: user.credits || 0,
      is_verified: user.is_verified ? 1 : 0
    });
    onEditModalOpen();
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // Handle number input changes
  const handleNumberChange = (name, value) => {
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // Submit user edit form
  const handleSubmitEdit = async () => {
    try {
      // Convert credits to number
      const userData = {
        ...editFormData,
        credits: parseInt(editFormData.credits, 10),
        is_verified: editFormData.is_verified === 1
      };

      await AdminService.updateUser(selectedUser.id, userData);
      
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === selectedUser.id ? { ...user, ...userData } : user
      ));
      
      setSuccessMessage('User updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      onEditModalClose();
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        User Management
      </Heading>
      
      {successMessage && (
        <Alert status="success" mb={4}>
          <AlertIcon />
          {successMessage}
        </Alert>
      )}
      
      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}
      
      <Box mb={4}>
        <InputGroup>
          <Input
            placeholder="Search by email, first name, or last name"
            value={searchTerm}
            onChange={handleSearchChange}
            pr="4.5rem"
          />
          <InputRightElement width="4.5rem">
            {searchTerm ? (
              <IconButton
                h="1.75rem"
                size="sm"
                icon={<FaTimes />}
                onClick={handleClearSearch}
                aria-label="Clear search"
              />
            ) : (
              <FaSearch />
            )}
          </InputRightElement>
        </InputGroup>
      </Box>
      
      <Box bg={bgColor} shadow="md" borderRadius="md" overflow="hidden">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Subscription</Th>
              <Th>Credits</Th>
              <Th>Created</Th>
              <Th>Last Login</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={9} textAlign="center">
                  <Spinner size="md" my={4} />
                </Td>
              </Tr>
            ) : users.length === 0 ? (
              <Tr>
                <Td colSpan={9} textAlign="center">
                  No users found
                </Td>
              </Tr>
            ) : (
              users.map((user) => (
                <Tr key={user.id}>
                  <Td>{user.id}</Td>
                  <Td>{user.email}</Td>
                  <Td>
                    {user.first_name || ''} {user.last_name || ''}
                  </Td>
                  <Td>
                    <Badge 
                      colorScheme={user.subscription_type === 'premium' ? 'purple' : 'gray'}
                      variant="solid"
                    >
                      {user.subscription_type === 'premium' ? 'Premium' : 'Free'}
                    </Badge>
                  </Td>
                  <Td>{user.credits}</Td>
                  <Td>{formatDate(user.created_at)}</Td>
                  <Td>{formatDate(user.last_login)}</Td>
                  <Td>
                    <Badge 
                      colorScheme={user.is_verified ? 'green' : 'yellow'}
                      variant="solid"
                    >
                      {user.is_verified ? 'Verified' : 'Unverified'}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      <IconButton
                        size="sm"
                        colorScheme="blue"
                        icon={<FaEdit />}
                        onClick={() => handleEditUser(user)}
                        aria-label="Edit user"
                      />
                      <Tooltip label="View user's search history">
                        <IconButton
                          as={Link}
                          to={`/admin?tab=search-history&user_id=${user.id}`}
                          size="sm"
                          colorScheme="teal"
                          icon={<FaHistory />}
                          aria-label="View search history"
                        />
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        
        <Flex justify="space-between" align="center" p={4}>
          <Box>
            <Select
              value={rowsPerPage}
              onChange={(e) => handleChangeRowsPerPage(e.target.value)}
              size="sm"
              width="100px"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </Select>
          </Box>
          
          <Flex>
            <Button
              size="sm"
              onClick={() => handleChangePage(page - 1)}
              isDisabled={page === 0}
              mr={2}
            >
              Previous
            </Button>
            <Text alignSelf="center" mx={2}>
              Page {page + 1} of {Math.ceil(totalUsers / rowsPerPage)}
            </Text>
            <Button
              size="sm"
              onClick={() => handleChangePage(page + 1)}
              isDisabled={page >= Math.ceil(totalUsers / rowsPerPage) - 1}
              ml={2}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Box>
      
      {/* Edit User Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedUser && (
              <Stack spacing={4}>
                <Text fontWeight="medium">
                  {selectedUser.email}
                </Text>
                
                <FormControl>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    name="first_name"
                    value={editFormData.first_name}
                    onChange={handleFormChange}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    name="last_name"
                    value={editFormData.last_name}
                    onChange={handleFormChange}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Subscription Type</FormLabel>
                  <Select
                    name="subscription_type"
                    value={editFormData.subscription_type}
                    onChange={handleFormChange}
                  >
                    <option value="free">Free</option>
                    <option value="premium">Premium</option>
                  </Select>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Credits</FormLabel>
                  <NumberInput
                    min={0}
                    value={editFormData.credits}
                    onChange={(value) => handleNumberChange('credits', value)}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Verification Status</FormLabel>
                  <Select
                    name="is_verified"
                    value={editFormData.is_verified}
                    onChange={handleFormChange}
                  >
                    <option value={1}>Verified</option>
                    <option value={0}>Unverified</option>
                  </Select>
                </FormControl>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditModalClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmitEdit}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserManagement;
